<div class="cfs">

  <!-- ══════════════════════════════════════════════════════════════════════════════════════════
       THE LIST
       ══════════════════════════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="!selectedForm">

    <div class="cfs__head">
      <div>
        <h2>Practice forms</h2>
        <p class="cfs__lede">
          Questionnaires your practice sends to patients - with the invite, when an
          appointment is booked, with a reminder, or by hand from the patient file.
        </p>
      </div>
      <button mat-flat-button color="primary" type="button" (click)="startCreate()"
              *ngIf="!isCreating">
        <mat-icon>add</mat-icon> New form
      </button>
    </div>

    <!-- Create. A form starts as a draft with no questions; the builder is where it gets them. -->
    <div class="cfs__create" *ngIf="isCreating">
      <mat-form-field appearance="outline" class="cfs__create-name">
        <mat-label>Form name</mat-label>
        <input matInput [(ngModel)]="newFormName" name="newFormName" maxlength="300"
               placeholder="e.g. Pre-operative screening"
               (keyup.enter)="createForm()">
        <mat-hint>The form is created as a draft. Nothing is sent until you publish it.</mat-hint>
      </mat-form-field>
      <button mat-flat-button color="primary" type="button" (click)="createForm()">Create</button>
      <button mat-button type="button" (click)="cancelCreate()">Cancel</button>
    </div>

    <!-- A refused delete, kept on screen: the count is the answer, not the failure. -->
    <div class="cfs__alert cfs__alert--error" *ngIf="deleteBlocked" data-test="delete-blocked">
      <mat-icon>pending_actions</mat-icon>
      <div>
        <strong>&ldquo;{{ deleteBlocked.formName }}&rdquo; cannot be deleted yet.</strong>
        <p>{{ deleteBlocked.message }}</p>
        <p class="cfs__count">
          <strong>{{ deleteBlocked.openRequestCount }}</strong>
          patient(s) have this form open. Revoke those requests from the patient file, or set the
          form to <em>Archived</em> instead - an archived form stops being sent but keeps
          everything already collected.
        </p>
      </div>
      <button mat-icon-button type="button" (click)="dismissDeleteBlocked()"
              aria-label="Dismiss">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div class="cfs__alert cfs__alert--warn" *ngIf="listError">
      <mat-icon>error_outline</mat-icon>
      <div>{{ listError }}</div>
    </div>

    <div class="cfs__loading" *ngIf="isLoading">
      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    </div>

    <p class="cfs__empty" *ngIf="!isLoading && rows.length === 0">
      No forms yet. Create one and it will appear here.
    </p>

    <div class="cfs__row" *ngFor="let row of rows; trackBy: trackByFormId">
      <div class="cfs__row-main" (click)="openForm(row.form.id)">
        <div class="cfs__row-title">
          <span class="cfs__name">{{ row.form.name }}</span>
          <span class="cfs__chip cfs__chip--{{ row.form.status }}">
            {{ statusLabel(row.form.status) }}
          </span>
          <span class="cfs__chip cfs__chip--muted" *ngIf="row.form.category">
            {{ row.form.category }}
          </span>
        </div>

        <div class="cfs__row-meta">
          <span *ngIf="row.currentVersionNumber !== null">
            <mat-icon>history</mat-icon> Version {{ row.currentVersionNumber }}
          </span>
          <span *ngIf="row.currentVersionNumber === null" class="cfs__meta--dim">
            <mat-icon>history_toggle_off</mat-icon> Never published
          </span>

          <span *ngIf="row.form.sendWithInvite">
            <mat-icon>mail</mat-icon> Sent with the patient invite
          </span>

          <span *ngIf="row.triggerCount > 0">
            <mat-icon>event_available</mat-icon>
            {{ row.triggerCount }} appointment-type trigger(s)
          </span>
          <span *ngIf="row.triggerCount === 0" class="cfs__meta--dim">
            <mat-icon>event_busy</mat-icon> No appointment triggers
          </span>

          <span *ngIf="row.form.requiresSignature">
            <mat-icon>draw</mat-icon> Signature required
          </span>
        </div>
      </div>

      <div class="cfs__row-actions">
        <button mat-icon-button type="button" matTooltip="Edit" aria-label="Edit form"
                (click)="openForm(row.form.id)">
          <mat-icon>edit</mat-icon>
        </button>
        <button mat-icon-button type="button" matTooltip="Duplicate" aria-label="Duplicate form"
                (click)="duplicateForm(row)">
          <mat-icon>content_copy</mat-icon>
        </button>
        <button mat-icon-button color="warn" type="button" matTooltip="Delete"
                aria-label="Delete form" (click)="deleteForm(row)">
          <mat-icon>delete_outline</mat-icon>
        </button>
      </div>
    </div>
  </ng-container>

  <!-- ══════════════════════════════════════════════════════════════════════════════════════════
       THE EDITOR
       ══════════════════════════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="selectedForm">

    <div class="cfs__head">
      <div class="cfs__back">
        <button mat-icon-button type="button" (click)="closeEditor()" aria-label="Back to forms">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <div>
          <h2>{{ selectedForm.name }}</h2>
          <p class="cfs__lede">
            <span class="cfs__chip cfs__chip--{{ selectedForm.status }}">
              {{ statusLabel(selectedForm.status) }}
            </span>
            <span *ngIf="selectedForm.currentVersion">
              Currently sending version {{ selectedForm.currentVersion.versionNumber }}.
            </span>
            <span *ngIf="!selectedForm.currentVersion">Never published.</span>
            <span class="cfs__unpublished" *ngIf="builderDirty">
              - you have unpublished changes.
            </span>
          </p>
        </div>
      </div>

      <!--
        IMPORT FROM PDF — offered on a draft only (§16.2 rule 1). See `canImportFromPdf` for why a
        published form does not get this button.
      -->
      <button mat-stroked-button type="button" *ngIf="canImportFromPdf"
              (click)="openImportDialog()" data-test="import-entry">
        <mat-icon>picture_as_pdf</mat-icon> Import from PDF
      </button>
    </div>

    <!-- ── Delivery settings ────────────────────────────────────────────────────────────── -->
    <mat-expansion-panel class="cfs__panel" [expanded]="true">
      <mat-expansion-panel-header>
        <mat-panel-title><mat-icon>tune</mat-icon> Delivery settings</mat-panel-title>
      </mat-expansion-panel-header>

      <form [formGroup]="settingsForm" class="cfs__settings">
        <div class="cfs__grid">
          <mat-form-field appearance="outline" class="cfs__grow">
            <mat-label>Name</mat-label>
            <input matInput formControlName="name" maxlength="300">
          </mat-form-field>

          <mat-form-field appearance="outline" class="cfs__grow">
            <mat-label>Category</mat-label>
            <input matInput formControlName="category" maxlength="100"
                   placeholder="e.g. Pre-op, Intake">
            <mat-hint>Your own wording. Used to group forms.</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="cfs__narrow">
            <mat-label>Status</mat-label>
            <mat-select formControlName="status">
              <mat-option *ngFor="let status of statuses; trackBy: trackByIndex"
                          [value]="status.value">
                {{ status.label }}
              </mat-option>
            </mat-select>
            <mat-hint>
              A form can only be marked published once it has a published version.
            </mat-hint>
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="cfs__full">
          <mat-label>Description (for your staff, not the patient)</mat-label>
          <textarea matInput formControlName="description" rows="2"></textarea>
        </mat-form-field>

        <div class="cfs__toggles">
          <mat-slide-toggle formControlName="sendWithInvite" *ngIf="!stateMode">
            Send with the patient invite
          </mat-slide-toggle>
          <mat-slide-toggle formControlName="allowManualSend" *ngIf="!stateMode">
            Allow staff to send it by hand from the patient file
          </mat-slide-toggle>
          <mat-slide-toggle formControlName="requiresSignature">
            Require a signature
          </mat-slide-toggle>
          <mat-slide-toggle formControlName="guardianMayComplete">
            In the patient app, a parent or guardian may complete it for a 12 to 17 year old
          </mat-slide-toggle>
          <mat-slide-toggle formControlName="active">
            Active
          </mat-slide-toggle>
        </div>

        <div class="cfs__grid">
          <mat-form-field appearance="outline" class="cfs__narrow">
            <mat-label>Link expires after (days)</mat-label>
            <input matInput type="number" formControlName="expiryDays" min="1" max="365">
          </mat-form-field>
        </div>

        <button mat-flat-button color="primary" type="button"
                [disabled]="isSavingSettings" (click)="saveSettings()">
          <mat-icon>save</mat-icon> Save settings
        </button>
        <p class="cfs__hint">
          These settings save straight away. The <strong>questions</strong> below are published as a
          version instead - that is what makes an already-answered form readable years later.
        </p>
      </form>
    </mat-expansion-panel>

    <!-- ── Appointment-type triggers ────────────────────────────────────────────────────── -->
    <mat-expansion-panel class="cfs__panel">
      <mat-expansion-panel-header>
        <mat-panel-title><mat-icon>event_available</mat-icon> Appointment triggers</mat-panel-title>
        <mat-panel-description>{{ links.length }} configured</mat-panel-description>
      </mat-expansion-panel-header>

      <div class="cfs__trigger" *ngFor="let link of links; trackBy: trackById"
           data-test="trigger-row">
        <mat-icon>{{ link.active ? 'event_available' : 'pause_circle_outline' }}</mat-icon>
        <div class="cfs__trigger-text">
          <strong>{{ link.appointmentType ? link.appointmentType.type : link.appointmentTypeId }}</strong>
          <span>{{ sendOnLabel(link.sendOn) }}</span>
          <span class="cfs__meta--dim" *ngIf="!link.active">Paused</span>
        </div>
        <button mat-icon-button color="warn" type="button"
                matTooltip="Remove this trigger" aria-label="Remove trigger"
                (click)="unlinkTrigger(link)">
          <mat-icon>link_off</mat-icon>
        </button>
      </div>

      <p class="cfs__empty" *ngIf="links.length === 0">
        This form is not sent for any appointment type.
      </p>

      <div class="cfs__grid cfs__trigger-add">
        <mat-form-field appearance="outline" class="cfs__grow">
          <mat-label>Appointment type</mat-label>
          <mat-select [(ngModel)]="linkAppointmentTypeId" [ngModelOptions]="{ standalone: true }">
            <mat-option *ngFor="let type of availableAppointmentTypes(); trackBy: trackById"
                        [value]="type.id">
              {{ type.type }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="cfs__grow">
          <mat-label>Send</mat-label>
          <mat-select [(ngModel)]="linkSendOn" [ngModelOptions]="{ standalone: true }">
            <mat-option *ngFor="let option of sendOns; trackBy: trackByIndex"
                        [value]="option.value">
              {{ option.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <button mat-stroked-button type="button"
                [disabled]="!linkAppointmentTypeId || isLinking"
                (click)="linkTrigger()">
          <mat-icon>add_link</mat-icon> Add trigger
        </button>
      </div>
    </mat-expansion-panel>

    <!-- ── Import from a PDF, and every attempt so far (§16) ────────────────────────────── -->
    <mat-expansion-panel class="cfs__panel">
      <mat-expansion-panel-header>
        <mat-panel-title><mat-icon>picture_as_pdf</mat-icon> Import from PDF</mat-panel-title>
        <mat-panel-description>{{ imports.length }} attempt(s)</mat-panel-description>
      </mat-expansion-panel-header>

      <p class="cfs__hint">
        A PDF questionnaire can be read into questions by AI. What comes back is an
        <strong>unreviewed draft</strong>: you check every question against the document in the
        editor below and publish it yourself. Nothing an import produces is ever sent to a patient
        on its own.
      </p>

      <button mat-stroked-button type="button" *ngIf="canImportFromPdf"
              (click)="openImportDialog()" data-test="import-panel-entry">
        <mat-icon>upload_file</mat-icon> Import from PDF
      </button>

      <!-- Said out loud rather than leaving a missing button to be puzzled over. -->
      <p class="cfs__hint" *ngIf="!canImportFromPdf" data-test="import-unavailable">
        Importing is offered on a form that has never been published, so a machine reading can never
        replace questions patients are already answering. Duplicate this form, or create a new one,
        and import into that.
      </p>

      <div class="cfs__loading" *ngIf="isLoadingImports">
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      </div>

      <p class="cfs__empty" *ngIf="!isLoadingImports && imports.length === 0">
        No documents have been imported into this form.
      </p>

      <div class="cfs__import" *ngFor="let entry of imports; trackBy: trackById"
           data-test="import-row">
        <mat-icon>{{ entry.status === 'failed' ? 'error_outline' : 'description' }}</mat-icon>
        <div class="cfs__import-text">
          <strong>{{ entry.sourceFileName }}</strong>
          <span class="cfs__chip cfs__chip--{{ entry.status }}" data-test="import-status">
            {{ importStatusLabel(entry) }}
          </span>
          <span class="cfs__meta--dim">
            {{ entry.createdAt | date: 'd MMM y, HH:mm' }}<span *ngIf="entry.pageCount">
              &middot; {{ entry.pageCount }} page(s)</span>
          </span>
          <!-- The server's own reason. A failed import that says nothing is a failed import twice. -->
          <span class="cfs__import-error" *ngIf="entry.status === 'failed' && entry.errorMessage"
                data-test="import-row-error">
            {{ entry.errorMessage }}
          </span>
        </div>
        <!--
          Offered for a FAILED attempt that kept its draft too (§16.2 rule 2) — that is the
          repairable case, and refusing to open it would mean re-paying for the pages.
        -->
        <button mat-button type="button" *ngIf="canReviewImport(entry)"
                (click)="reviewImport(entry)" data-test="import-review">
          <mat-icon>fact_check</mat-icon>
          {{ entry.status === 'failed' ? 'Repair in editor' : 'Review in editor' }}
        </button>
      </div>
    </mat-expansion-panel>

    <!-- ── Version history — read-only, newest first ────────────────────────────────────── -->
    <mat-expansion-panel class="cfs__panel">
      <mat-expansion-panel-header>
        <mat-panel-title><mat-icon>history</mat-icon> Version history</mat-panel-title>
        <mat-panel-description>{{ versions.length }} version(s)</mat-panel-description>
      </mat-expansion-panel-header>

      <p class="cfs__hint">
        A version is a permanent record of the questions as they were asked. It can never be edited
        - editing the questions below and publishing writes a <strong>new</strong> version.
      </p>

      <div class="cfs__loading" *ngIf="isLoadingVersions">
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      </div>

      <p class="cfs__empty" *ngIf="!isLoadingVersions && versions.length === 0">
        Nothing published yet.
      </p>

      <div class="cfs__version" *ngFor="let version of versions; trackBy: trackById"
           data-test="version-row">
        <div class="cfs__version-text">
          <strong>Version {{ version.versionNumber }}</strong>
          <span class="cfs__chip cfs__chip--published" *ngIf="isCurrentVersion(version)">
            Being sent now
          </span>
          <span class="cfs__meta--dim">{{ version.publishedAt | date: 'd MMM y, HH:mm' }}</span>
        </div>
        <div class="cfs__version-actions">
          <button mat-button type="button" (click)="viewVersion(version)">
            <mat-icon>visibility</mat-icon> View
          </button>
          <button mat-button type="button" (click)="copyVersionIntoBuilder(version)">
            <mat-icon>content_copy</mat-icon> Copy into editor
          </button>
        </div>
      </div>

      <!-- A version, rendered read-only by the same renderer the patient uses. -->
      <div class="cfs__version-view" *ngIf="viewedVersion">
        <div class="cfs__version-view-head">
          <strong>Version {{ viewedVersion.versionNumber }} - as it was asked</strong>
          <button mat-icon-button type="button" (click)="closeVersion()" aria-label="Close version">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <div class="cfs__loading" *ngIf="isLoadingVersion">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        </div>
        <app-dynamic-form-renderer
          *ngIf="!isLoadingVersion && viewedSchema"
          [schema]="viewedSchema"
          [readonly]="true">
        </app-dynamic-form-renderer>
      </div>
    </mat-expansion-panel>

    <!-- ── The builder ──────────────────────────────────────────────────────────────────── -->
    <div class="cfs__builder">
      <app-custom-form-builder
        [form]="selectedForm"
        [initialSchema]="builderSchema"
        [importedDraft]="importedDraftNotice"
        (dirtyChange)="onBuilderDirtyChange($event)"
        (published)="onPublished($event)">
      </app-custom-form-builder>
    </div>
  </ng-container>
</div>
